/**
 * Dropdown turns a native `<select>` into a Bootstrap dropdown rendered from
 * its options (#650). The hidden select stays the source of truth — it keeps
 * its value, participates in forms, and fires a bubbling `change` when an item
 * is chosen — and the dropdown follows it: programmatic `value`/`selectedIndex`
 * writes, `change` events, and option-list mutations all re-render. Unlike
 * `<option>`s, menu items honor per-option inline styles in every browser
 * (Safari and Firefox ignore `<option>` styling).
 */
export default class Dropdown {
    readonly $select: HTMLSelectElement;
    readonly $toggle: HTMLButtonElement;
    readonly $menu: HTMLUListElement;

    #$label: HTMLSpanElement;
    #$items: HTMLButtonElement[] = [];

    constructor($select: HTMLSelectElement) {
        this.$select = $select;

        this.$toggle = document.createElement('button');
        this.$toggle.type = 'button';
        this.$toggle.className = $select.className;
        this.$toggle.classList.add('text-start');
        const style = $select.getAttribute('style');
        if (style !== null) {
            this.$toggle.setAttribute('style', style);
        }
        this.$toggle.setAttribute('data-bs-toggle', 'dropdown');
        this.$toggle.setAttribute('aria-expanded', 'false');

        this.#$label = document.createElement('span');
        this.$toggle.appendChild(this.#$label);

        const labelIDs: string[] = [];
        for (const $label of Array.from($select.labels ?? [])) {
            if (!$label.id) {
                $label.id = `${$select.id}-label-${labelIDs.length}`;
            }
            labelIDs.push($label.id);
        }
        if (labelIDs.length) {
            this.$toggle.setAttribute('aria-labelledby', labelIDs.join(' '));
        }

        this.$menu = document.createElement('ul');
        this.$menu.className = 'dropdown-menu';

        $select.classList.add('d-none');
        $select.after(this.$toggle, this.$menu);

        // Programmatic writes to these fire no event, so shadow the prototype
        // accessors on this instance to catch them.
        for (const property of ['value', 'selectedIndex'] as const) {
            const descriptor = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, property);
            if (!descriptor?.get || !descriptor.set) {
                continue;
            }
            const nativeGet = descriptor.get.bind($select) as () => string | number;
            const nativeSet = descriptor.set.bind($select) as (value: string | number) => void;
            Object.defineProperty($select, property, {
                get: nativeGet,
                set: (value: string | number) => {
                    nativeSet(value);
                    this.#renderSelection();
                },
            });
        }

        $select.addEventListener('change', () => this.#renderSelection());

        new MutationObserver(() => this.#render()).observe($select, {
            childList: true,
            subtree: true,
            attributes: true,
            characterData: true,
        });

        this.#render();
    }

    #render() {
        this.$menu.textContent = '';
        this.#$items = [];

        for (const [i, $option] of Array.from(this.$select.options).entries()) {
            const $li = document.createElement('li');
            const $item = document.createElement('button');
            $item.type = 'button';
            $item.className = 'dropdown-item';
            const optionStyle = $option.getAttribute('style');
            if (optionStyle !== null) {
                $item.setAttribute('style', optionStyle);
            }
            $item.disabled = $option.disabled;
            $item.textContent = $option.textContent;
            $item.addEventListener('click', () => {
                this.$select.selectedIndex = i;
                this.$select.dispatchEvent(new Event('change', { bubbles: true }));
            });
            this.#$items.push($item);
            $li.appendChild($item);
            this.$menu.appendChild($li);
        }

        this.#renderSelection();
    }

    #renderSelection() {
        const $option = this.$select.selectedOptions[0];
        this.#$label.textContent = $option?.textContent ?? '';
        this.#$label.setAttribute('style', $option?.getAttribute('style') ?? '');
        this.#$items.forEach(($item, i) => {
            $item.classList.toggle('active', i === this.$select.selectedIndex);
        });
    }
}
